import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

type IntroAnimationProps = {
  words: string[]
  wordColors?: string[]
  backgroundGradient?: {
    top: string
    bottom: string
  }
  onComplete?: () => void
  onHeroStart?: () => void
}

const LETTER_INTERVAL = 200
const LETTER_START = 400
const HOLD_AFTER_ALL = 400
const LETTERS_FADE_DUR = 300
const WIPE_DURATION = 1000
const HERO_START_OFFSET = 200

const IntroAnimation = ({
  words,
  wordColors,
  backgroundGradient,
  onComplete,
  onHeroStart,
}: IntroAnimationProps) => {

  const [showIntro, setShowIntro] = useState(true)
  const [visibleCount, setVisibleCount] = useState(0)
  const [hideLetters, setHideLetters] = useState(false)
  const [startReveal, setStartReveal] = useState(false)

  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])

  // -----------------------------
  // RESET ON MOUNT (IMPORTANT)
  // -----------------------------
  useEffect(() => {
    setShowIntro(true)
    setVisibleCount(0)
    setHideLetters(false)
    setStartReveal(false)

    timersRef.current.forEach(clearTimeout)
    timersRef.current = []
  }, [])

  const mid = Math.ceil(words.length / 2)
  const line1 = words.slice(0, mid).join('')
  const line2 = words.slice(mid).join('')
  const maxLen = Math.max(line1.length, line2.length)

  // -----------------------------
  // TIMELINE
  // -----------------------------
  useEffect(() => {
    const push = (fn: () => void, delay: number) => {
      const id = setTimeout(fn, delay)
      timersRef.current.push(id)
    }

    const totalSteps = maxLen

    const allShownAt = LETTER_START + (totalSteps - 1) * LETTER_INTERVAL
    const hideLettersAt = allShownAt + HOLD_AFTER_ALL
    const wipeStartAt = hideLettersAt + LETTERS_FADE_DUR
    const heroStartAt = wipeStartAt + HERO_START_OFFSET
    const introHideAt = wipeStartAt + WIPE_DURATION + 200

    for (let step = 1; step <= totalSteps; step++) {
      push(
        () => setVisibleCount(step),
        LETTER_START + (step - 1) * LETTER_INTERVAL
      )
    }

    push(() => setHideLetters(true), hideLettersAt)
    push(() => setStartReveal(true), wipeStartAt)
    push(() => onHeroStart?.(), heroStartAt)

    push(() => {
      setShowIntro(false)
      onComplete?.()
    }, introHideAt)

    return () => timersRef.current.forEach(clearTimeout)
  }, [maxLen, onHeroStart, onComplete])

  const renderLine = (line: string, lineIndex: number) => (
    <div
      className="
        flex items-center justify-center
        gap-[1vw] md:gap-[0.8vw]
        w-full px-3
      "
    >
      {Array.from({ length: maxLen }).map((_, i) => {
        const char = line[i] || ' '
  
        const color = wordColors?.[lineIndex] || '#8FE64A'
  
        return (
          <motion.span
            key={i}
            initial={{ opacity: 0 }}
            animate={{
              opacity: hideLetters
                ? 0
                : visibleCount > i
                ? 1
                : 0,
            }}
            transition={{
              duration: 0.35,
              ease: 'easeInOut',
            }}
            className="
              font-black leading-none
              whitespace-pre
            "
            style={{
              color,
              fontFamily: "'raj', sans-serif",
  
              /* 🔥 auto scale by screen */
              fontSize: `clamp(
                20px,
                ${Math.max(4, 90 / maxLen)}vw,
                108px
              )`,
            }}
          >
            {char}
          </motion.span>
        )
      })}
    </div>
  )

  return (
    <AnimatePresence>
      {showIntro && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* BACKGROUND */}
          <motion.div
            className="absolute inset-0 overflow-hidden"
            initial={{ y: '0%' }}
            animate={
              startReveal
                ? {
                    y: '-102%',
                    borderBottomLeftRadius: '100%',
                    borderBottomRightRadius: '100%',
                  }
                : { y: '0%' }
            }
            transition={{ duration: WIPE_DURATION / 1000, ease: 'easeIn' }}
          >
            <div
              className="h-full w-full"
              style={{
                background: backgroundGradient
                  ? `linear-gradient(to bottom, ${backgroundGradient.top}, ${backgroundGradient.bottom})`
                  : '#000',
              }}
            />
          </motion.div>

          {/* TEXT */}
          <div className="relative z-10 flex w-full flex-col items-center gap-2 px-4">
            {renderLine(line1, 0)}
            {renderLine(line2, 1)}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default IntroAnimation